import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Users, 
    Trash2, 
    Search,
    Mail,
    CheckCircle2,
    XCircle,
    Coins,
    Loader2,
    ShieldCheck,
    User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminCustomersPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Benutzer konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteDialog.user) return;
        
        setDeleting(true);
        try {
            const response = await axios.delete(
                `${API}/admin/users/${deleteDialog.user.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(
                `${deleteDialog.user.name} wurde gelöscht. ` +
                `${response.data.deleted_referrals} Empfehlungen entfernt.`
            );
            
            // Remove user from list
            setUsers(users.filter(u => u.id !== deleteDialog.user.id));
            setDeleteDialog({ open: false, user: null });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Löschen fehlgeschlagen');
        } finally {
            setDeleting(false);
        }
    };

    const filteredUsers = users.filter(user => {
        const search = searchTerm.toLowerCase();
        return (
            user.name?.toLowerCase().includes(search) ||
            user.email?.toLowerCase().includes(search)
        );
    });

    const customers = filteredUsers.filter(u => u.role === 'customer');
    const admins = filteredUsers.filter(u => u.role === 'org_admin');

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Kunden</h1>
                        <p className="text-slate-600 mt-1">
                            Verwalten Sie alle registrierten Kunden
                        </p>
                    </div>
                    
                    {/* Search */}
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                            data-testid="search-users-input"
                        />
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div 
                                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                >
                                    <Users size={24} style={{ color: tenant?.primary_color }} />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{users.length}</p>
                                    <p className="text-sm text-slate-500">Benutzer gesamt</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                                    <User size={24} className="text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{customers.length}</p>
                                    <p className="text-sm text-slate-500">Kunden</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                                    <ShieldCheck size={24} className="text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{admins.length}</p>
                                    <p className="text-sm text-slate-500">Administratoren</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Users Table */}
                <Card data-testid="users-table-card">
                    <CardHeader>
                        <CardTitle>Alle Benutzer</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {filteredUsers.length === 0 ? (
                            <div className="text-center py-12">
                                <Users size={48} className="mx-auto text-slate-300 mb-3" />
                                <p className="text-slate-500">Keine Benutzer gefunden</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Benutzer</TableHead>
                                            <TableHead>E-Mail verifiziert</TableHead>
                                            <TableHead>Rolle</TableHead>
                                            <TableHead>Punkte</TableHead>
                                            <TableHead className="text-right">Aktion</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredUsers.map((user) => (
                                            <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div 
                                                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                                            style={{ backgroundColor: tenant?.primary_color || '#3b82f6' }}
                                                        >
                                                            {user.name?.charAt(0)?.toUpperCase() || '?'}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{user.name}</p>
                                                            <p className="text-sm text-slate-500 flex items-center gap-1">
                                                                <Mail size={12} />
                                                                {user.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {user.email_verified ? (
                                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                                            <CheckCircle2 size={12} className="mr-1" />
                                                            Verifiziert
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                                            <XCircle size={12} className="mr-1" />
                                                            Ausstehend
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {user.role === 'org_admin' ? (
                                                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                                            <ShieldCheck size={12} className="mr-1" />
                                                            Admin
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                                                            <User size={12} className="mr-1" />
                                                            Kunde
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Coins size={14} className="text-amber-500" />
                                                        <span className="font-medium">
                                                            {user.points_balance?.toLocaleString('de-DE') || 0}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {user.role === 'customer' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setDeleteDialog({ open: true, user })}
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            data-testid={`delete-user-${user.id}`}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie <strong>{deleteDialog.user?.name}</strong> ({deleteDialog.user?.email}) wirklich löschen?
                            <br /><br />
                            <span className="text-red-600 font-medium">
                                Achtung: Alle Empfehlungen und Einlösungen dieses Benutzers werden ebenfalls gelöscht!
                            </span>
                            <br /><br />
                            Die E-Mail-Adresse wird wieder freigegeben und kann für eine neue Registrierung verwendet werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                    Löschen...
                                </>
                            ) : (
                                <>
                                    <Trash2 size={16} className="mr-2" />
                                    Löschen
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardLayout>
    );
};
